import { useRef, type ReactNode } from 'react';
import * as D from '@radix-ui/react-dialog';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import { Button } from './Button';
import { useT } from '@hana/i18n';
import s from './kit.module.css';

interface OverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** một dòng mô tả (đọc cho trình đọc màn hình) */
  description?: string;
  children?: ReactNode;
  /** chân: nút; nút chủ chốt đặt cuối, là động từ cụ thể */
  footer?: ReactNode;
}

/** Nhớ phần tử đang giữ focus lúc mở để trả lại khi đóng (lớp mở bằng mã, không có Trigger). */
function useRestoreFocus() {
  const from = useRef<HTMLElement | null>(null);
  return {
    onOpen: () => {
      from.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    },
    onClose: (e: Event) => {
      const el = from.current;
      from.current = null;
      if (el?.isConnected && el !== document.body) {
        e.preventDefault();
        el.focus();
      }
    },
  };
}

/** Mở lớp nổi: đưa con trỏ vào ô nhập đầu tiên (không có thì để Radix chọn). */
function focusFirstField(e: Event) {
  const root = e.currentTarget as HTMLElement | null;
  const el = root?.querySelector<HTMLElement>('[data-ov-body] :is(input:not([readonly]), select, textarea)');
  if (el) {
    e.preventDefault();
    el.focus();
  }
}

function Head({ title, icon, closeLabel }: { title: string; icon?: ReactNode; closeLabel?: string }) {
  const tr = useT();
  return (
    <div className={s.ovHead}>
      {icon}
      <D.Title className={s.ovTitle}>{title}</D.Title>
      <D.Close asChild>
        <Button variant="ghost" aria-label={closeLabel ?? tr('common.close')} icon={<IconX size={18} aria-hidden="true" />} />
      </D.Close>
    </div>
  );
}

/** Modal nhỏ · 2–5 trường, rộng 520. Xác nhận việc không hoàn tác: `danger`. */
export function Modal({ open, onOpenChange, title, description, children, footer, danger }: OverlayProps & { danger?: boolean }) {
  const rf = useRestoreFocus();
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className={s.scrim} />
        <D.Content className={s.modal} onOpenAutoFocus={(e) => {
            rf.onOpen();
            focusFirstField(e);
          }}
          onCloseAutoFocus={rf.onClose}
        >
          <Head
            title={title}
            icon={
              danger ? (
                <span className={s.ovIcon} aria-hidden="true">
                  <IconAlertTriangle size={18} />
                </span>
              ) : undefined
            }
          />
          <div className={s.ovBody} data-ov-body>
            {description ? <D.Description className={s.ovDesc}>{description}</D.Description> : <D.Description hidden>{title}</D.Description>}
            {children}
          </div>
          {footer && <div className={s.ovFoot}>{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Ngăn kéo phải · 6+ trường, 640px, chân luôn hiện nút. Điện thoại: bảng trượt từ đáy. */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  closeLabel,
  readableBackground,
}: OverlayProps & {
  /** nhãn nút đóng theo board (vd "Đóng hồ sơ tàu (Esc)") */
  closeLabel?: string;
  /** nền phía sau vẫn đọc được (không aria-hidden / inert), scrim vẽ riêng — theo board có ngăn kéo đè danh sách */
  readableBackground?: boolean;
}) {
  const rf = useRestoreFocus();
  return (
    <D.Root open={open} onOpenChange={onOpenChange} modal={!readableBackground}>
      <D.Portal>
        {readableBackground ? (
          <div className={s.scrim} aria-hidden="true" onClick={() => onOpenChange(false)} />
        ) : (
          <D.Overlay className={s.scrim} />
        )}
        <D.Content className={s.drawer} onOpenAutoFocus={(e) => {
            rf.onOpen();
            focusFirstField(e);
          }}
          onCloseAutoFocus={rf.onClose}
        >
          <Head title={title} closeLabel={closeLabel} />
          <div className={s.ovBody} data-ov-body>
            {description ? <D.Description className={s.ovDesc}>{description}</D.Description> : <D.Description hidden>{title}</D.Description>}
            {children}
          </div>
          {footer && <div className={s.ovFoot}>{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
